import { Notify } from "@/components/ui/notify";
import { formatQuantity } from "@/lib/quantity-format";
import type {
  ImportOriginPreview,
  ImportSuspiciousDays,
  TranslatedRow,
} from "@/types/data-import.types";
import { PRODUCTION_ROLE_LABELS } from "@/types/parameters.types";

interface TranslatedPreviewProps {
  rows: TranslatedRow[];
  ignoredColumns: string[];
  /** Spec 084 — quantas linhas cada ligação vai casar, contadas sobre o arquivo INTEIRO. */
  originPreview?: ImportOriginPreview[];
  /** Spec 084 (FR-028) — dias que entrariam com numerador e sem base. Aviso, não bloqueio. */
  suspiciousDays?: ImportSuspiciousDays[];
}

const ROLE_LABEL = PRODUCTION_ROLE_LABELS;

/**
 * Spec 082 — o que EXATAMENTE entra na base se o gestor executar. Mostra o valor cru ao lado do
 * convertido, porque é a conversão que ele precisa conferir: doze pacotes de meio quilo viram seis
 * quilos, e um travessão significa que não houve registro — nunca zero.
 */
export function TranslatedPreview({
  rows,
  ignoredColumns,
  originPreview = [],
  suspiciousDays = [],
}: TranslatedPreviewProps) {
  if (rows.length === 0) {
    return (
      <Notify.Empty
        title="Nada traduzido ainda"
        description="Aponte pelo menos uma coluna para uma linha, um produto e um sentido para ver o que vai entrar."
      />
    );
  }

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-md border border-border-default">
        <table className="w-full text-[12px]">
          <caption className="sr-only">
            Amostra do arquivo já traduzida pelo mapeamento escolhido
          </caption>
          <thead className="border-b border-border-default bg-bg-muted-rd uppercase tracking-wider text-text-secondary">
            <tr>
              <th className="px-2 py-1.5 text-left">Linha</th>
              <th className="px-2 py-1.5 text-left">Dia</th>
              <th className="px-2 py-1.5 text-left">Linha de produção</th>
              <th className="px-2 py-1.5 text-left">Produto</th>
              <th className="px-2 py-1.5 text-left">Sentido</th>
              <th className="px-2 py-1.5 text-right">No arquivo</th>
              <th className="px-2 py-1.5 text-right">Na unidade da linha</th>
            </tr>
          </thead>
          <tbody>
            {rows.flatMap((row) =>
              row.entries.map((e, i) => (
                <tr
                  key={`${row.line}-${e.column}-${e.role}-${i}`}
                  className="border-b border-border-default last:border-0"
                >
                  <td className="px-2 py-1.5 font-mono text-text-tertiary">{row.line}</td>
                  <td className="px-2 py-1.5">{row.day ?? "—"}</td>
                  <td className="px-2 py-1.5">{e.lineName}</td>
                  <td className="px-2 py-1.5">{e.productName}</td>
                  <td className="px-2 py-1.5">{ROLE_LABEL[e.role]}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{e.rawValue || "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {/* `formatQuantity` é a fonte única da escala (3 casas, sem zeros à direita):
                        `toLocaleString` cru mostrava aqui um número diferente do que a barra
                        lateral mostra para a MESMA quantidade. */}
                    {e.quantityInLineUnit === null
                      ? "—"
                      : `${formatQuantity(e.quantityInLineUnit)} ${e.unit}`}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {rows.some((r) => r.issues.length > 0) ? (
        <Notify.Banner variant="warning" title="Linhas com problema nesta amostra">
          <ul className="ml-4 list-disc space-y-0.5">
            {rows
              .filter((r) => r.issues.length > 0)
              .slice(0, 5)
              .map((r) =>
                r.issues.map((issue, i) => (
                  <li key={`${r.line}-${i}`}>
                    Linha {r.line}
                    {issue.column ? ` · ${issue.column}` : ""}: {issue.reason}
                  </li>
                ))
              )}
          </ul>
        </Notify.Banner>
      ) : null}

      {originPreview.length > 0 ? (
        <ul className="space-y-0.5 text-[12px] text-text-secondary">
          {originPreview.map((o) => (
            <li key={`${o.upstreamLineName}-${o.downstreamLineName}`}>
              <span className="text-text-primary">
                {o.upstreamLineName} → {o.downstreamLineName}
              </span>
              : {o.associated} linha(s) com origem
              {/* Contado à parte porque é o aviso: essas bateladas vão ficar sem origem (FR-024d). */}
              {o.withoutUpstream > 0
                ? ` · ${o.withoutUpstream} sem a etapa anterior, que ficam sem origem`
                : ""}
            </li>
          ))}
        </ul>
      ) : null}

      {suspiciousDays.length > 0 ? (
        <Notify.Banner variant="warning" title="Dias que entrariam sem base para o indicador">
          <ul className="ml-4 list-disc space-y-0.5">
            {suspiciousDays.map((s) => (
              <li key={s.indicatorId}>
                {s.indicatorName}: {s.absent.length} dia(s) sem base
                {s.zero.length > 0 ? ` e ${s.zero.length} com base zerada` : ""}. O arquivo entra
                assim mesmo — confira a planilha antes de executar.
              </li>
            ))}
          </ul>
        </Notify.Banner>
      ) : null}

      {ignoredColumns.length > 0 ? (
        <p className="text-[12px] text-text-secondary">
          {ignoredColumns.length} coluna(s) ignorada(s): {ignoredColumns.join(", ")}.
        </p>
      ) : null}
    </div>
  );
}
